/**
 * Network Status Component
 * مكون لعرض حالة الاتصال بالشبكة
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated, I18nManager, Platform } from 'react-native';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import { Ionicons } from '@expo/vector-icons';
import logger from '../utils/logger';

interface NetworkStatusProps {
  showOnlyOffline?: boolean;
  position?: 'top' | 'bottom';
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({
  showOnlyOffline = true,
  position = 'top',
}) => {
  const [isConnected, setIsConnected] = useState<boolean | null>(true);
  const [connectionType, setConnectionType] = useState<string>('');
  const slideAnim = useRef(new Animated.Value(-60)).current;
  const isRTL = I18nManager.isRTL;

  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
      const connected = state.isConnected ?? true;
      setIsConnected(connected);
      setConnectionType(state.type);

      logger.info('Network status changed:', {
        isConnected: connected,
        type: state.type,
        isInternetReachable: state.isInternetReachable,
      });
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isConnected) {
      // Show banner
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: Platform.OS !== 'web',
        tension: 100,
        friction: 10,
      }).start();
    } else if (showOnlyOffline) {
      // Hide banner
      Animated.timing(slideAnim, {
        toValue: -60,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
  }, [isConnected, showOnlyOffline, slideAnim]);

  // If online and showOnlyOffline, don't render
  if (isConnected && showOnlyOffline) {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        position === 'bottom' ? styles.bottomPosition : styles.topPosition,
        {
          transform: [{ translateY: slideAnim }],
          backgroundColor: isConnected ? '#10B981' : '#EF4444',
        },
      ]}
    >
      <View style={[styles.content, { flexDirection: 'row' }]}>
        <Ionicons name={isConnected ? 'wifi' : 'cloud-offline'} size={18} color="#FFFFFF" />
        <Text style={[styles.text, { marginLeft: isRTL ? 0 : 8, marginRight: isRTL ? 8 : 0 }]}>
          {isConnected
            ? isRTL
              ? 'متصل بالإنترنت'
              : 'Connected'
            : isRTL
            ? 'لا يوجد اتصال بالإنترنت'
            : 'No Internet Connection'}
        </Text>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1000,
    paddingVertical: 12,
    paddingHorizontal: 16,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  },
  topPosition: {
    top: 0,
  },
  bottomPosition: {
    bottom: 0,
  },
  content: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default NetworkStatus;
